import { NgClass, AsyncPipe } from '@angular/common'
import { Component, DestroyRef, LOCALE_ID, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router'
import { Subscription } from 'rxjs'

import { FilterMatchMode, MenuItem, TreeNode, PrimeTemplate } from 'primeng/api'
import { Button } from 'primeng/button'
import { PrimeNG } from 'primeng/config'
import { ConfirmDialog } from 'primeng/confirmdialog'
import { Dialog } from 'primeng/dialog'
import { Drawer } from 'primeng/drawer'
import { DialogService } from 'primeng/dynamicdialog'
import { Ripple } from 'primeng/ripple'
import { SelectButton } from 'primeng/selectbutton'
import { StyleClass } from 'primeng/styleclass'
import { Tag } from 'primeng/tag'
import { Toast } from 'primeng/toast'
import { MsalService } from '@azure/msal-angular'
import { AuthenticationResult } from '@azure/msal-browser'

import { environment } from '@environments/environment'
import { find } from 'lodash-es'

import { newUser, User } from '@app/interfaces/user'
import { LongRunningJobService } from '@app/services/long-running-job.service'
import { NetworkService } from '@app/services/network.service'
import { UpdateService } from '@app/services/update.service'
import { UserService } from '@app/services/user.service'
import { clearCache } from '@app/components/generic/utilities'
import { TranslatedLongRunningJob } from '@app/components/long-running-jobs/long-running-job'
import { UpdateComponent } from '@app/components/updates/updates-detail'
import { stripMsalResponseParams } from '@app/auth/msal-url'

import { DraftsComponent } from './components/drafts/drafts.component'
import { LongRunningJobsComponent } from './components/long-running-jobs/long-running-jobs-detail'

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrl: 'app.component.less',
  encapsulation: ViewEncapsulation.None,
  imports: [
    Drawer,
    PrimeTemplate,
    RouterLink,
    NgClass,
    Ripple,
    StyleClass,
    DraftsComponent,
    SelectButton,
    FormsModule,
    Button,
    Tag,
    RouterOutlet,
    LongRunningJobsComponent,
    Toast,
    ConfirmDialog,
    Dialog,
    AsyncPipe,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly primeng = inject(PrimeNG)
  private readonly authService = inject(MsalService)
  private readonly router = inject(Router)
  private readonly longRunningJobService = inject(LongRunningJobService)
  private readonly updateService = inject(UpdateService)
  private readonly dialogService = inject(DialogService)
  public readonly locale = inject(LOCALE_ID)
  public readonly networkService = inject(NetworkService)
  public readonly userService = inject(UserService)
  private readonly destroyRef = inject(DestroyRef)

  private userSubscription!: Subscription
  private lrjSubscription!: Subscription
  public appEnvironment: string = 'dev'
  public user: User = newUser()
  public longRunningJobs: TranslatedLongRunningJob[] = []
  public languageOptions: { label: string, value: string }[] = [{ label: 'EN', value: 'en' }, { label: 'FR', value: 'fr' }]
  public currentUrlRoot: string = ''
  public sidebarVisible: boolean = true
  public initialUrlRoot: string = ''
  public menuNodes: TreeNode[] = []
  public userMenuItems: MenuItem[] = []
  public gitVersionsVisible: boolean = false

  protected readonly theEnvironment = environment

  constructor () {
    // listen for url changes and update the `currentUrlRoot` to style the correct menu button
    this.router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        const parser = document.createElement('a')
        parser.href = val.url
        // remove the locale from the url to allow the `startsWith()` method to be used for url root matching
        this.currentUrlRoot = parser.pathname.replace(/^\/en\//, '/').replace(/^\/fr\//, '/')
        // record the initial url path to pre-open the correct menu group
        if (!this.initialUrlRoot) this.initialUrlRoot = this.currentUrlRoot
      }
    })
  }

  ngOnInit () {
    // hide the 'date is not' primeng filter option which isn't useful
    // https://github.com/primefaces/primeng/blob/master/src/app/components/api/primengconfig.ts#L30
    this.primeng.filterMatchModeOptions.date = [
      FilterMatchMode.DATE_IS,
      FilterMatchMode.DATE_BEFORE,
      FilterMatchMode.DATE_AFTER,
    ]
    this.primeng.filterMatchModeOptions.text = [
      FilterMatchMode.CONTAINS,
      FilterMatchMode.EQUALS,
      FilterMatchMode.NOT_EQUALS,
      FilterMatchMode.STARTS_WITH,
      FilterMatchMode.ENDS_WITH,
    ]
    this.primeng.filterMatchModeOptions.numeric = [
      FilterMatchMode.EQUALS,
      FilterMatchMode.NOT_EQUALS,
      FilterMatchMode.LESS_THAN,
      FilterMatchMode.LESS_THAN_OR_EQUAL_TO,
      FilterMatchMode.GREATER_THAN,
      FilterMatchMode.GREATER_THAN_OR_EQUAL_TO,
    ]

    // subscribe to any changes of the currently-logged-in user
    this.userSubscription = this.userService.getUser().subscribe(
      user => { this.user = user },
    )

    // subscribe to any changes of the user's currently running LRJs, to reflect them
    // in the spinner widgets
    this.lrjSubscription = this.longRunningJobService.getJobs().subscribe(
      jobs => { this.longRunningJobs = jobs },
    )

    // once-off build the sidebar menu
    this.buildSidebarMenu()

    // cable up the MSAL redirect (handleRedirectObservable initializes the client itself)
    this.authService.handleRedirectObservable().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result: AuthenticationResult | null) => {
        this.clearMsalResponseFromUrl()
        if (result) this.userService.reloadPermissions()
      },
      error: (error) => {
        console.warn('in app component msal error, will call userService.clearUser() and refresh')
        console.warn(error)
        // there was a problem logging in with Azure SSO, fallback to clearing caches and refresh
        // https://stackoverflow.com/questions/66405214/browserautherror-interaction-in-progress-interaction-is-currently-in-progress
        this.userService.clearUser()
        // reload without the auth response params, so the retry does not trip over them again
        window.location.replace(stripMsalResponseParams(window.location.href))
      },
    })

    // once-off setup config for primeng, mainly for translations
    this.primeng.setTranslation({
      dayNamesMin: [
        $localize`:@@prime-sunday-short:Su`,
        $localize`:@@prime-monday-short:Mo`,
        $localize`:@@prime-tuesday-short:Tu`,
        $localize`:@@prime-wednesday-short:We`,
        $localize`:@@prime-thursday-short:Th`,
        $localize`:@@prime-friday-short:Fr`,
        $localize`:@@prime-saturday-short:Sa`,
      ],
      monthNames: [
        $localize`:@@prime-january:January`,
        $localize`:@@prime-february:February`,
        $localize`:@@prime-march:March`,
        $localize`:@@prime-april:April`,
        $localize`:@@prime-may:May`,
        $localize`:@@prime-june:June`,
        $localize`:@@prime-july:July`,
        $localize`:@@prime-august:August`,
        $localize`:@@prime-september:September`,
        $localize`:@@prime-october:October`,
        $localize`:@@prime-november:November`,
        $localize`:@@prime-december:December`,
      ],
      monthNamesShort: [
        $localize`:@@prime-january-short:Jan`,
        $localize`:@@prime-february-short:Feb`,
        $localize`:@@prime-march-short:Mar`,
        $localize`:@@prime-april-short:Apr`,
        $localize`:@@prime-may-short:May`,
        $localize`:@@prime-june-short:Jun`,
        $localize`:@@prime-july-short:Jul`,
        $localize`:@@prime-august-short:Aug`,
        $localize`:@@prime-september-short:Sep`,
        $localize`:@@prime-october-short:Oct`,
        $localize`:@@prime-november-short:Nov`,
        $localize`:@@prime-december-short:Dec`,
      ],
      startsWith: $localize`:@@prime-startsWith:Starts with`,
      contains: $localize`:@@prime-contains:Contains`,
      notContains: $localize`:@@prime-notContains:Not contains`,
      endsWith: $localize`:@@prime-endsWith:Ends with`,
      equals: $localize`:@@prime-equals:Equals`,
      notEquals: $localize`:@@prime-notEquals:Not equals`,
      noFilter: $localize`:@@prime-noFilter:No Filter`,
      lt: $localize`:@@prime-lt:Less than`,
      lte: $localize`:@@prime-lte:Less than or equal to`,
      gt: $localize`:@@prime-gt:Greater than`,
      gte: $localize`:@@prime-gte:Greater than or equal to`,
      is: $localize`:@@prime-is:Is`,
      isNot: $localize`:@@prime-isNot:Is not`,
      before: $localize`:@@prime-before:Before`,
      after: $localize`:@@prime-after:After`,
      dateIs: $localize`:@@prime-dateIs:Date is`,
      dateIsNot: $localize`:@@prime-dateIsNot:Date is not`,
      dateBefore: $localize`:@@prime-dateBefore:Date is before`,
      dateAfter: $localize`:@@prime-dateAfter:Date is after`,
      clear: $localize`:@@prime-clear:Clear`,
      apply: $localize`:@@prime-apply:Apply`,
      matchAll: $localize`:@@prime-matchAll:Match all`,
      matchAny: $localize`:@@prime-matchAny:Match any`,
      addRule: $localize`:@@prime-addRule:Add rule`,
      removeRule: $localize`:@@prime-removeRule:Remove rule`,
      accept: $localize`:@@prime-accept:Yes`,
      reject: $localize`:@@prime-reject:No`,
      dateFormat: 'yy-mm-dd',
      firstDayOfWeek: 1,
      today: $localize`:@@prime-today:Today`,
      emptyMessage: $localize`:@@prime-emptyMessage:No results found`,
      emptyFilterMessage: $localize`:@@prime-emptyFilterMessage:No results found`,
    })

    // used for the watermark
    if (environment.production) {
      this.appEnvironment = 'production'
    }

    this.updateService.getUpdateAvailable().subscribe((updateIsAvailable: boolean) => {
      if (!updateIsAvailable) return
      this.dialogService.open(UpdateComponent, {
        header: $localize`:@@topbar-update-dialog-title:Update available`,
        width: '30rem',
        focusOnShow: false,
      })
    })
  }

  /**
   * Drop auth response parameters from the address bar once MSAL has consumed them.
   *
   * MSAL only clears the hash, so the `?state=...` Entra appends when redirecting back from a
   * logout lingers. Signing in from that url makes it the url MSAL returns to after the code
   * exchange, where the stale state is re-read as a response and fails with `state_mismatch`.
   */
  private clearMsalResponseFromUrl () {
    const cleaned = stripMsalResponseParams(window.location.href)
    if (cleaned === window.location.href) return

    // go through the router rather than history.replaceState: this runs during bootstrap, and the
    // router's initial navigation still holds the original url and would put the params straight back
    const { pathname, search, hash } = new URL(cleaned)
    void this.router.navigateByUrl(`${pathname}${search}${hash}`, { replaceUrl: true })
  }

  ngOnDestroy () {
    this.userSubscription?.unsubscribe()
    this.lrjSubscription?.unsubscribe()
  }

  /**
   * Define the menu structure according to PrimeNG TreeNodes
   */
  buildSidebarMenu () {
    this.menuNodes = [{
      label: $localize`:@@sidebar-home:Home`,
      data: {
        initialRouteGroup: 'home',
        currentUrlRoot: '/',
        hasPermissionIn: [],
        routerLink: ['/'],
        negativeStyle: true,
        iconClass: 'fa-solid fa-house',
      },
    }, {
      label: $localize`:@@sidebar-admin:Administration`,
      data: {
        initialRouteGroup: 'admin',
        hasPermissionIn: ['view_archiveuserconfiguration'],
        iconClass: 'fa-solid fa-lock',
      },
      children: [{
        label: $localize`:@@sidebar-user-configurations:User access`,
        data: {
          currentUrlRoot: '/admin/user-configurations/list',
          routerLink: ['admin', 'user-configurations', 'list'],
          hasPermissionIn: ['view_archiveuserconfiguration'],
          iconClass: 'fa-solid fa-user',
        },
      }, {
        label: $localize`:@@sidebar-access-tokens:API access tokens`,
        data: {
          currentUrlRoot: '/admin/access-tokens/list',
          routerLink: ['admin', 'access-tokens', 'list'],
          hasPermissionIn: ['view_archivesaccesstoken'],
          iconClass: 'fa-solid fa-unlock',
        },
      }],
    }, {
      label: $localize`:@@sidebar-search:Search`,
      data: {
        initialRouteGroup: 'search',
        hasPermissionIn: ['view_archivedocument'],
        iconClass: 'fa-solid fa-magnifying-glass',
      },
      children: [{
        label: $localize`:@@sidebar-search-tree:Search via tree`,
        data: {
          currentUrlRoot: '/search/tree',
          routerLink: ['search', 'tree'],
          hasPermissionIn: ['view_archivedocument'],
          iconClass: 'fa-solid fa-folder-tree',
        },
      }, {
        label: $localize`:@@sidebar-search-list:Search via list`,
        data: {
          currentUrlRoot: '/search/list',
          routerLink: ['search', 'list'],
          hasPermissionIn: ['view_archivedocument'],
          iconClass: 'fa-solid fa-list',
        },
      }],
    }]
  }

  /**
   * Return whether the given 'named group' matches the current route: used for pre-opening a menu group if we are deep-linked to it
   */
  initialRouteWasInGroup (groupName: string): boolean {
    let possibleRoots: string[] = []

    if (groupName === 'search') {
      possibleRoots = ['/', '/search/']
    } else if (groupName === 'admin') {
      possibleRoots = ['/admin/']
    }

    return !!find(possibleRoots, (root: string) => {
      return this.initialUrlRoot.startsWith(root)
    })
  }

  getUserInitials () {
    if (this.user.anonymous) return ''
    const initials = (this.user.first_name ?? '').substring(0, 1) + (this.user.last_name ?? '').substring(0, 1)
    return initials.toUpperCase()
  }

  onChangeLang ($event: { originalEvent?: Event, value?: string }) {
    if (!$event.value) return
    const newLocale = $event.value.toLocaleLowerCase()
    const baseUrl = window.location.origin
    const newLocation = window.location.href.replace(`${baseUrl}/${this.locale}/`, `${baseUrl}/${newLocale}/`)
    if (newLocation.startsWith(`${baseUrl}/`)) {
      window.location.href = newLocation
    }
  }

  onClickLogout () {
    clearCache()
    this.authService.logoutRedirect()
  }

  onClearCache () {
    clearCache()
  }

  // successLoginLogout (result: AuthenticationResult) {
  //   if (result) {
  //     this.authService.instance.setActiveAccount(result.account)
  //     const username = this.authService.instance.getAllAccounts()[0].localAccountId
  //     this.usersApi.retrieve(username).subscribe((res: User) => {
  //       this.userService.setUser(res)
  //     })
  //   } else {
  //     // we arrive here when the app starts, check here if there is a currently-logged-in 'Microsoft' user from local storage
  //     if (this.authService.instance.getAllAccounts().length === 0) {
  //       // if not, delete 'Acted' the user object
  //       this.userService.clearUser()
  //     }
  //   }
  // }
}
